import React, { FC, useState } from 'react';
import styles from './RangePicker.module.scss';
import { PropertyType } from '../../../common';
import { endOfDay, startOfDay, format, addMonths, isSameDay, endOfYear } from 'date-fns';
import useFilters from '../../../common/hooks/useFilters';
import { ClickDetector, DateRange } from 'esa-ui-library';

interface RangePickerProps {
	startDate?: Date;
	endDate?: Date;
}

const RangePicker: FC<RangePickerProps> = ({ startDate = new Date(), endDate = new Date() }): JSX.Element => {
	const [opened, setOpened] = useState(false);
	const [start, setStart] = useState(startOfDay(startDate));
	const [end, setEnd] = useState(endOfDay(endDate));
	const [activeLabel, setActiveLabel] = useState(format(startDate, 'd MMM yyyy'));
	const { onFilter } = useFilters();

	const openHandler = (e: React.MouseEvent<HTMLInputElement>) => {
		e.stopPropagation();
		setOpened(true);
	};

	const setCurrent = (dates: PropertyType) => {
		const { startDate, endDate, customRange } = dates;
		setStart(startDate);
		setEnd(endDate);
		if (customRange) return setActiveLabel(customRange);
		getLabel(startDate, endDate);
	};

	const onDateChange = (dates: PropertyType) => {
		setCurrent(dates);
		const filterValue = [
			{
				name: 'start_date',
				value: dates.startDate,
			},
			{
				name: 'end_date',
				value: dates.endDate,
			},
		];
		onFilter(filterValue);
		setOpened(false);
	};

	const getLabel = (startDate: number | Date, endDate: number | Date) => {
		const label = isSameDay(startDate, endDate)
			? format(startDate, 'd MMM yyyy')
			: `${format(startDate, 'd MMM yyyy')} - ${format(endDate, 'd MMM yyyy')}`;
		setActiveLabel(label);
	};

	return (
		<section className={styles.RangePicker}>
			<ClickDetector onOutsideClick={() => setOpened(false)} dataId='detector'>
				<>
					<span onClick={openHandler} className={styles.PickerLabel}>
						<span>{activeLabel}</span>
					</span>
					{opened && (
						<section onClick={(e) => e.stopPropagation()} className={styles.PickerContent}>
							<DateRange
								start={start}
								end={end}
								onChange={onDateChange}
								onCancel={() => setOpened(false)}
								months={2}
								maxDate={endOfYear(addMonths(new Date(), 6))}
								customRanges
								time
							/>
						</section>
					)}
				</>
			</ClickDetector>
		</section>
	);
};

export { RangePicker };
